import * as React from 'react'
import { useMemo } from 'react'
import { darken, useTheme } from '@mui/material/styles'
import { HeaderContainer } from '@user/components/DetailPages/HeaderContainer'
import { IOpportunityDetail } from '@user/store/opportunity/typings'
import { Chevron } from '@sales-center/components'
import { OPPORTUNITY_STATUSES } from '@user/components/Opportunities/OpportunityCard/typings'
import { getColorByOpportunityStatus } from '@user/components/Opportunities/DetailPage/utils'
import { ClosedDecoration } from '@user/components/EntityCards/ClosedDecoration'
import { CancelServicesButton } from '@user/components/Opportunities/OpportunityActions/CancelServicesButton'
import { CancelServicesDialog } from '@user/components/SoldServices/CancelServices'
import { SoldServicesDetails } from '../SoldServicesDetails/SoldServicesDetails'
import { SoldServiceStatus } from '../SoldServicesStatus'
import { soldServiceBackgroundColor } from '../utils/soldServiceBackgroundColor'
import { soldServiceStatusColor } from '../SoldServicesCardFooter/soldServiceStatusColor'

import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import { soldServiceStatusToReOpenPopupVariant } from '@user/components/Opportunities/ReOpenOppPopup/typings'
import { useDialogState } from '@user/utils/useDialogState'
import { useToggleBoolean } from '@user/utils/useToggleBoolean'
import { ReOpenOppPopupNew } from '@user/components/Opportunities/ReOpenOppPopup/ReOpenOppPopupNew'

interface Props {
  detail: IOpportunityDetail
  status: OPPORTUNITY_STATUSES
}

const styles = {
  statusLabel: {
    fontFamily: 'Source Sans Pro Semibold',
    fontSize: 20,
  },
}

export function SoldServicesOppHeader(props: Props) {
  const { detail, status } = props
  const { id, soldServiceStatus, closedLostReason, closedDate } = detail

  const [open, onClick] = useToggleBoolean()
  const [reOpenOpen, showReOpenPopup, hideReOpenPopup] = useDialogState()
  const theme = useTheme()
  const backgroundColor = useMemo(
    () =>
      open
        ? theme.palette.common.white
        : status === OPPORTUNITY_STATUSES.Won
          ? soldServiceBackgroundColor(soldServiceStatus)
          : getColorByOpportunityStatus(status),
    [open, theme.palette.common.white, status, soldServiceStatus],
  )

  const statusBarColor = open
    ? theme.palette.common.white
    : soldServiceBackgroundColor(soldServiceStatus)

  const textColor = darken(
    soldServiceStatusColor(soldServiceStatus, theme),
    0.6,
  )

  const reOpenPopupVariant = useMemo(
    () => soldServiceStatusToReOpenPopupVariant(soldServiceStatus),
    [soldServiceStatus],
  )

  return (
    <>
      {detail.closedDate && (
        <HeaderContainer
          color={
            status == 'lost' ? backgroundColor : theme.palette.common.white
          }
          hideBorder
          paddingY={'10px'}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <ClosedDecoration
              status={status}
              date={closedDate}
              reason={closedLostReason || ''}
            />
            {status === OPPORTUNITY_STATUSES.Lost && (
              <CancelServicesButton
                opportunityId={id}
                soldServiceStatus={soldServiceStatus}
              />
            )}
          </Box>
        </HeaderContainer>
      )}
      {status === OPPORTUNITY_STATUSES.Lost && (
        <CancelServicesDialog opportunityId={id} />
      )}
      {detail.soldServiceStatus !== 'NotSent' && (
        <>
          <HeaderContainer
            color={statusBarColor}
            dataTestId="soldServicesStatusHeader"
            hideBorder
            justifyContent="space-between"
            onClick={onClick}
            paddingY={'10px'}
          >
            <Box sx={styles.statusLabel} mr={1}>
              <Chevron
                open={open}
                dataTestId="soldServicesStatusExpandChevron"
              />
              Status
            </Box>
            {!open && (
              <Box mr={5}>
                <SoldServiceStatus
                  hideTitle
                  textColor={textColor}
                  soldServiceStatus={soldServiceStatus}
                />
              </Box>
            )}
          </HeaderContainer>
          <Box style={{ borderBottom: open ? 'solid 1px #ddd' : 'none' }}>
            <Collapse in={open} unmountOnExit>
              <SoldServicesDetails
                {...props}
                opportunityId={id}
                onReOpenClick={showReOpenPopup}
                soldServiceStatus={soldServiceStatus}
              />
            </Collapse>
          </Box>

          {reOpenOpen && (
            <ReOpenOppPopupNew
              updateOppDetail
              open={reOpenOpen}
              opportunityId={id}
              onClose={hideReOpenPopup}
              variant={reOpenPopupVariant}
              salesFunnelId={detail.salesFunnelId}
              salesTeamId={detail.salesTeamId.toString()}
              assigneeId={
                detail.assigneeId === '00000000-0000-0000-0000-000000000000'
                  ? null
                  : detail.assigneeId
              }
            />
          )}
        </>
      )}
    </>
  )
}
